import { Shelf, Configuration } from '@/types/Configuration';
import { Subsidiary } from '@/types/Customer';

interface PlayerCounts {
  online: number;
  total: number;
}

export interface TransformedSubsidiary extends Subsidiary {
  companyName: string;
  unitId: string;
  playerTotals: PlayerCounts;
  productType?: string;
  changedAt?: string;
  changedBy?: string;
  slideCount?: number;
}

const getShelfPlayerCounts = (shelf: Shelf): PlayerCounts => {
  if (!shelf?.players) return { online: 0, total: 0 };

  const onlinePlayers = shelf.players.filter(
    (player) => player?.isOnline,
  ).length;
  const totalPlayers = shelf.players.length;

  return { online: onlinePlayers, total: totalPlayers };
};

const calculatePlayerTotals = (shelves: Shelf[] = []): PlayerCounts => {
  return shelves.reduce(
    (acc: PlayerCounts, shelf) => {
      const counts = getShelfPlayerCounts(shelf);
      return {
        online: acc.online + counts.online,
        total: acc.total + counts.total,
      };
    },
    { online: 0, total: 0 },
  );
};

const transformSubsidiary = (
  subsidiary: Subsidiary,
  companyName: string,
  unitId: string,
  productType?: string,
  changedAt?: string,
  changedBy?: string,
  slideCount?: number,
): TransformedSubsidiary => ({
  companyName,
  ...subsidiary,
  playerTotals: calculatePlayerTotals(subsidiary?.shelves),
  unitId,
  productType,
  changedAt,
  changedBy,
  slideCount,
});

const filterUnique = (
  items: TransformedSubsidiary[],
): TransformedSubsidiary[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.companyName;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const processSortedData = (
  data: Configuration[] = [],
): TransformedSubsidiary[] => {
  const transformedData = data.flatMap((item) =>
    item.subsidiaries.map((subsidiary) =>
      transformSubsidiary(
        subsidiary,
        item.name,
        item.id,
        item.productType,
        item.changedAt,
        item.changedBy,
        item.slideCount,
      ),
    ),
  );
  return filterUnique(transformedData);
};
